import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { capability, managedService } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const email = capability('email')!
const managedEmail = managedService('managed-email')

const INLINE_LINK =
  'text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-4'

const PROBLEMS = [
  {
    problem: 'Social reach keeps shrinking',
    description:
      'Your followers see a fraction of what you post, and the algorithm decides which fraction. Email goes to everyone who asked for it, every time.',
  },
  {
    problem: 'Your list lives in three places',
    description:
      'Ticket buyers in one spreadsheet, donors in another tool, newsletter subscribers in a third. Nobody knows which names overlap.',
  },
  {
    problem: 'Campaigns take a back seat to the season',
    description:
      'Season announcements, ticket launches, and reminders all land during your busiest weeks. The email that would sell the concert never gets written.',
  },
  {
    problem: 'Generic tools do not know music',
    description:
      'Mailchimp does not know what a season, a program, or a call time is. You end up fighting the tool instead of writing to your audience.',
  },
]

const FAQS = [
  {
    q: 'What does the self-serve plan include?',
    a: `Newsletters, sequences, and automations for up to 2,000 contacts at $${email.priceMonthly} a month, with a step-up to 10,000 contacts at $35. No send metering, so a busy concert week never costs extra.`,
  },
  {
    q: 'What does Managed Email look like month to month?',
    a: `We plan, write, design, and send about 2 campaigns a month plus 1 automation for $${managedEmail.priceMonthly} a month, timed to your season: announcements, on-sale pushes, concert reminders, year-end appeals. The software is included.`,
  },
  {
    q: 'Can we start self-serve and hand it off later?',
    a: 'Yes. Everything runs on the same system, so moving between running it yourself and having us run it is a conversation, not a migration.',
  },
  {
    q: 'What about our existing list?',
    a: 'Simple imports are part of getting set up. Large or messy list migrations are scoped separately, and exporting your list back out is free, always.',
  },
]

export default function EmailMarketingPage() {
  const breadcrumbs = generateBreadcrumbs('/services/email-marketing', breadcrumbLabels)

  useSEO({
    title: 'Email Marketing for Music Organizations',
    // Price interpolates from pricing.ts; it was a literal $25 here.
    description: `Newsletters, sequences, and season campaigns for music organizations. Run it yourself from $${email.priceMonthly}/mo, or have Fugue Lab plan, write, and send for you.`,
    url: '/services/email-marketing',
    canonical: '/services/email-marketing',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Email Marketing for Music Organizations',
      description:
        'Email marketing software and managed campaign services for festivals, churches, choruses, and music schools.',
      url: '/services/email-marketing',
    }),
  })

  // The hero keeps its entrance; everything below the fold is static. The
  // per-section staggered reveals and card hover lifts were the stock kit.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-16 md:space-y-36 md:px-12">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Email marketing that fills seats</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your audience already said yes once. Email is how you bring them back: season announcements, on-sale days,
          concert reminders, and appeals that land when they should.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Run it yourself for ${email.priceMonthly} a month, or have us plan, write, and send it for you. Either way it
          runs on the same system as your website, donations, and ticketing.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/pricing">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              See pricing
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems: a typographic list, name left and explanation right, in
          place of four bordered cards. Space alone separates the rows. */}
      <section aria-labelledby="problems" className="space-y-12">
        <h2 id="problems" className="text-fluid-h3 max-w-3xl">
          Why email, and why here
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {PROBLEMS.map((item) => (
            <li key={item.problem} className="grid gap-3 md:grid-cols-12 md:items-baseline md:gap-8">
              <h3 className="text-fluid-h4 md:col-span-5">{item.problem}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.description}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Unified CRM differentiator */}
      <section aria-labelledby="one-record" className="grid gap-6 md:grid-cols-12 md:gap-8">
        <h2 id="one-record" className="text-fluid-h3 md:col-span-5">
          One audience, one record
        </h2>
        <div className="text-muted-foreground space-y-4 leading-relaxed md:col-span-7">
          <p className="text-fluid-body max-w-[60ch]">
            Your subscribers, donors, and ticket buyers are the same people. Because email runs on the same system as
            your giving and ticketing, every name is one record: you can email everyone who bought a ticket last season
            but has not given, without exporting a single spreadsheet.
          </p>
          <p className="max-w-[60ch]">
            Point tools like Mailchimp plus Donorbox mean two lists that drift apart. Here they cannot drift, because
            they are the same list. See also{' '}
            <Link to="/services/donor-management" className={INLINE_LINK}>
              Donor Management
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Pricing. Two genuinely comparable plans, so they stay side by side on
          a plain ground; the recommended one is set apart by a raised ground,
          not a tint, glow or hover lift. */}
      <section aria-labelledby="pricing" className="space-y-12">
        <h2 id="pricing" className="text-fluid-h3">
          Two ways to run it
        </h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-surface flex flex-col rounded-md p-8">
            <h3 className="text-fluid-h4">{email.name}</h3>
            <p className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-5xl leading-none">${email.priceMonthly}</span>
              <span className="text-muted-foreground">/month</span>
            </p>
            <ul className="text-muted-foreground mt-6 mb-8 flex-1 space-y-2 leading-relaxed">
              {[
                'Newsletters, sequences, automations',
                'Up to 2,000 contacts ($35/mo up to 10,000)',
                'No send metering',
                'Self-serve, available today',
              ].map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/get-started" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                Get Started
              </Button>
            </Link>
          </div>
          <div className="bg-card flex flex-col rounded-md p-8">
            <h3 className="text-fluid-h4">{managedEmail.name}</h3>
            <p className="text-muted-foreground mt-1 text-sm">Done for you</p>
            <p className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-5xl leading-none">${managedEmail.priceMonthly}</span>
              <span className="text-muted-foreground">/month</span>
            </p>
            <ul className="text-muted-foreground mt-6 mb-8 flex-1 space-y-2 leading-relaxed">
              {[
                'We plan, write, design, and send',
                'About 2 campaigns per month plus 1 automation',
                'Timed to your season',
                'Email software included',
              ].map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/services/done-for-you#managed-email" className="block">
              <Button className="w-full cursor-pointer">Learn More</Button>
            </Link>
          </div>
        </div>
        <p className="text-muted-foreground text-sm">
          Product details live on the{' '}
          <Link to="/platform/features/email" className={INLINE_LINK}>
            Email feature page
          </Link>
          . Pay annually on any plan and get 2 months free.
        </p>
      </section>

      {/* FAQ */}
      <section aria-labelledby="questions" className="space-y-12">
        <h2 id="questions" className="text-fluid-h3">
          Questions
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {FAQS.map((f) => (
            <li key={f.q} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl leading-snug md:col-span-5">{f.q}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{f.a}</p>
            </li>
          ))}
        </ul>
        <div className="space-y-3">
          <a href="/book" target="_blank" rel="noopener noreferrer" className="inline-block">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
